import { Box, Grid, useMediaQuery } from '@mui/material'
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import gaiaTheme from '../../../../root-components/theme';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { SearchBar } from '../../../presentation-components/display-elements-components/search-bar';
import EnergySupplierService from '../../../../shared/services/energy-supplier/energy-supplier.service';
import { IEnergySupplier, IPostEnergySupplier } from '../../../../shared/services/energy-supplier/energy-supplier.model';
import { EnergySupplierDialog } from '../../../dialogs/super-admin/energy-supplier-dialog';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { useDebounce } from '../../../../hooks/use-debounce-hook';

export const SuperAdminEnergySuppliersPage = () => {
  const navigate = useNavigate();
  //Services
  const energySupplierService = new EnergySupplierService()
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [energySuppliers, setEnergySuppliers] = useState<IEnergySupplier[]>()
  const [searchQuery, setSearchQuery] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedEnergySupplierId, setSelectedEnergySupplierId] = useState<string>()
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  //Hooks
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  //Ref
  const requestId = useRef<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  //Use Effects
  useEffect(() => {
    getAllEnergySuppliers()
  }, [inputPage, perPage, change])

  const getAllEnergySuppliers = () => {
    requestId.current++
    const currentRequestId = requestId.current
    energySupplierService.getAllWithPagination(debouncedSearchQuery, inputPage, perPage, orderBy, order)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
          setTotalRecords(response.count);
          setEnergySuppliers(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
        }
        console.log(err)
      })
  }

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy, debouncedSearchQuery])

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    // { id: 'details', numeric: false, label: 'Details', sortable: true },
  ];

  const rows = !energySuppliers ? [] : energySuppliers.map(energySupplier => {
    return {
      id: energySupplier.id,
      action: (
        <TableActionButtons
          id={energySupplier.id}
          includeEdit={true}
          includeView={true}
          onEdit={() => editClickHandler(energySupplier.id)}
          onView={() => navigate(`/super-admin/energy-suppliers/${energySupplier.id}`)}
        />
      ),
      name: energySupplier.name,
      details: energySupplier.details,
    }
  });

  // Table Event Handlers
  const editClickHandler = (energySupplierId: string) => {
    setSelectedEnergySupplierId(energySupplierId)
    setOpenDialog(true)
  }

  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const submitHandler = (values: IPostEnergySupplier, resetForm: () => void, isEdit: boolean, energySupplierId?: string) => {
    if (isEdit && energySupplierId) {
      energySupplierService.edit(energySupplierId, values)
        .then(response => {
          getAllEnergySuppliers()
          setOpenDialog(false)
          resetForm()
          setIsDialogSubmitting(false)
          userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
        })
        .catch(err => {
          console.log(err)
          setIsDialogSubmitting(false)
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        })
    } else {
      setIsDialogSubmitting(true)
      energySupplierService.create(values)
        .then(response => {
          setOpenDialog(false)
          getAllEnergySuppliers()
          resetForm()
          setIsDialogSubmitting(false)
          userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
        })
        .catch(err => {
          console.log(err)
          setIsDialogSubmitting(false)
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        })
    }
  }


  return (
    <PageOuterContainer>
      <PageHeading
        title={'Energy Suppliers'}
        includeActionButton={true}
        includeBackButton={true}
        backButtonLink='/super-admin'
        backButtonText='Back to super admin'
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => setOpenDialog(true)}
        actionButtonText={`Add Energy Supplier`}
      />
      <Box sx={{ width: '100%' }}>
        <Grid container sx={{ mb: 2 }}>
          <Grid
            item
            xl={4} lg={4} md={6} sm={12} xs={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              width: '100%'
            }}>
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </Grid>
        </Grid>
        <LoadingPage pageReady={!isLoading}>
          {energySuppliers &&
            <TableDisplay
              onOrderChange={handleOrderChange}
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={rows}
              selectByIcon={true}
              selected={selected}
              setSelected={setSelected}
              inputPage={inputPage}
              setInputPage={setInputPage}
              totalRecords={totalRecords}
              perPage={perPage}
              setPerPage={setPerPage} />}
        </LoadingPage>
        {openDialog && selectedEnergySupplierId &&
          <EnergySupplierDialog
            open={openDialog}
            closeHandler={() => setOpenDialog(false)}
            isEdit={true}
            energySupplierId={selectedEnergySupplierId}
            isSubmitting={isDialogSubmitting}
            onSubmit={submitHandler}
          />
        }
        {openDialog && !selectedEnergySupplierId &&
          <EnergySupplierDialog
            open={openDialog}
            closeHandler={() => setOpenDialog(false)}
            isEdit={false}
            isSubmitting={isDialogSubmitting}
            onSubmit={submitHandler}
          />
        }
      </Box>
    </PageOuterContainer>
  )
}